/**
 * Per-class PLAYER action clips (AP5 player combat clips, 2026-09-23) — which
 * one-shot the player rig plays for `attack` / `gather` / `craft`, and at what
 * rate, keyed by WHAT the player is swinging or harvesting. ONE data table
 * (`PLAYER_ACTION_CLIPS`) + one pure resolver; nothing here is a branch per
 * weapon.
 *
 * Why a table on top of the pattern resolver (clips.ts): the player rig now
 * ships a clip per weapon class (`Attack_sword_1h`, `Attack_warhammer_2h`, …),
 * per gather kind (`Gather_mine`, …) and a crafting clip (`Craft_station`), and
 * each has its own authored CONTACT frame. The generic `attack` policy (one rate,
 * 5/3, measured on `Left_Slash`) lands contact inside the server's swing cooldown
 * for the fast classes only — so the rate has to be per clip, and it is DERIVED
 * here from each clip's contact time and the server cooldown, never chosen.
 *
 * Fallback (the never-blank rule): a rig without the class clip, an unknown class,
 * or no weapon at all resolves through the existing pattern path
 * (`resolveActionClipFor` + `DEFAULT_ACTION_POLICY`) — byte-for-byte the pre-table
 * behaviour, so `Left_Slash` / `Collect_Object` remain what an older rig plays.
 *
 * Players only. Enemies keep `MOTION_ATTACK_POLICY` / `resolveAttackMotionClip`
 * (AD-636) — nothing here is consulted on the creature path.
 *
 * Pure + node-safe (AD-120): no `three`, no DOM. Import boundary (AD-122): shared
 * values via `@great-cloud/shared/client`. Off the per-tick path (AD-031):
 * resolution happens on an action TRIGGER, never per frame.
 */

import type { GatheringSkill } from '@great-cloud/shared/client';

import {
  DEFAULT_ACTION_POLICY,
  PLAYER_ATTACK_COOLDOWN_SECONDS,
  normaliseClipName,
  resolveActionClipFor,
  type RigAction,
  type RigActionPolicy,
} from './clips.js';

// ---------------------------------------------------------------------------
// The server cadence the attack rate answers to (mirrored in clips.ts, re-exported).
// ---------------------------------------------------------------------------

export { PLAYER_ATTACK_COOLDOWN_SECONDS, PLAYER_ATTACK_COOLDOWN_TICKS_MIRROR } from './clips.js';

/**
 * The fastest a class attack clip is played to make its WHOLE motion fit the server
 * cadence (`q-player-attack-no-restart`, owner ruling 2026-09-25). `[tune]` — the owner's
 * eyeball decides whether 3× still reads as a swing. Every served class clip fits under it
 * today (2.28×–3.06×); a clip that would need more plays at this cap (or faster, if its
 * contact needs it) and the swing gate chains the next swing after it (swingGate.ts).
 */
export const ATTACK_RATE_READABLE_MAX = 3.2;

// ---------------------------------------------------------------------------
// Variants: what the player swings / harvests / works at.
// ---------------------------------------------------------------------------

/** The six player weapon classes the rig ships an attack clip for. */
export type WeaponClass = 'sword' | 'warhammer' | 'bow' | 'staff' | 'dagger' | 'shield';
export const WEAPON_CLASSES: readonly WeaponClass[] = [
  'sword',
  'warhammer',
  'bow',
  'staff',
  'dagger',
  'shield',
];

/** The gather motions the rig ships (no `fish`: there is no fishing tool). */
export type GatherKind = 'mine' | 'chop' | 'harvest';
export const GATHER_KINDS: readonly GatherKind[] = ['mine', 'chop', 'harvest'];

/** The crafting motions the rig ships — one bench clip today. */
export type CraftKind = 'station';
export const CRAFT_KINDS: readonly CraftKind[] = ['station'];

/** The actions this table covers (`hit` / `dead` stay on the generic path). */
export type PlayerClassAction = 'attack' | 'gather' | 'craft';

/** One authored clip: its rig name and the two clip-second facts the rate is derived from. */
export interface PlayerActionClipSpec {
  /** The clip name in the player rig GLB. */
  clip: string;
  /** Authored length, CLIP seconds at rate 1 (30 fps; in place). */
  lengthSeconds: number;
  /**
   * The authored contact frame, CLIP seconds at rate 1 — the blow landing (attack),
   * the arrow's release (bow), the tool striking (gather), the work beat (craft).
   */
  contactSeconds: number;
}

/**
 * THE table. Clip facts as authored for the player rig (30 fps, in place). Add a
 * class by adding a row (and its variant to the type above) — never a branch.
 */
export const PLAYER_ACTION_CLIPS: {
  readonly attack: Readonly<Record<WeaponClass, PlayerActionClipSpec>>;
  readonly gather: Readonly<Record<GatherKind, PlayerActionClipSpec>>;
  readonly craft: Readonly<Record<CraftKind, PlayerActionClipSpec>>;
} = {
  attack: {
    sword: { clip: 'Attack_sword_1h', lengthSeconds: 1.367, contactSeconds: 0.533 },
    warhammer: { clip: 'Attack_warhammer_2h', lengthSeconds: 1.467, contactSeconds: 1.233 },
    bow: { clip: 'Attack_bow', lengthSeconds: 1.633, contactSeconds: 1.333 },
    staff: { clip: 'Attack_staff', lengthSeconds: 1.833, contactSeconds: 1.167 },
    dagger: { clip: 'Attack_dagger', lengthSeconds: 1.4, contactSeconds: 0.433 },
    shield: { clip: 'Attack_shield', lengthSeconds: 1.5, contactSeconds: 0.6 },
  },
  gather: {
    mine: { clip: 'Gather_mine', lengthSeconds: 2.2, contactSeconds: 1.367 },
    chop: { clip: 'Gather_chop', lengthSeconds: 1.8, contactSeconds: 1.133 },
    harvest: { clip: 'Gather_harvest', lengthSeconds: 1.9, contactSeconds: 1.3 },
  },
  craft: {
    station: { clip: 'Craft_station', lengthSeconds: 2.4, contactSeconds: 1.567 },
  },
};

/**
 * COMBO chains per weapon class (AD-670; clips + facts from the art handover AD-9008,
 * `docs/art/attack-combos-handover.json`): step 1 is the class clip above; later steps are
 * library clips shipped in the `attack-combos` pack, hip-facing ramped so A → B → C → A
 * chains without a pop. Consecutive swings the SwingGate chains walk the steps; a pause
 * returns to step 1. Every row obeys the AD-664 rule (whole clip inside the cadence).
 * Gaps (art §3): dagger, shield and bow have two steps.
 */
export const PLAYER_ATTACK_COMBOS: Readonly<Record<WeaponClass, readonly PlayerActionClipSpec[]>> =
  {
    sword: [
      PLAYER_ACTION_CLIPS.attack.sword,
      { clip: 'Attack_sword_1h_2', lengthSeconds: 0.9, contactSeconds: 0.633 },
      { clip: 'Attack_sword_1h_3', lengthSeconds: 1.2, contactSeconds: 0.433 },
    ],
    dagger: [
      PLAYER_ACTION_CLIPS.attack.dagger,
      { clip: 'Attack_dagger_2', lengthSeconds: 1.0, contactSeconds: 0.333 },
    ],
    shield: [
      PLAYER_ACTION_CLIPS.attack.shield,
      { clip: 'Attack_shield_2', lengthSeconds: 1.3, contactSeconds: 0.4 },
    ],
    warhammer: [
      PLAYER_ACTION_CLIPS.attack.warhammer,
      { clip: 'Attack_warhammer_2h_2', lengthSeconds: 1.6, contactSeconds: 0.567 },
      { clip: 'Attack_warhammer_2h_3', lengthSeconds: 1.8, contactSeconds: 1.033 },
    ],
    staff: [
      PLAYER_ACTION_CLIPS.attack.staff,
      { clip: 'Attack_staff_2', lengthSeconds: 1.9, contactSeconds: 0.5 },
      { clip: 'Attack_staff_3', lengthSeconds: 1.9, contactSeconds: 0.933 },
    ],
    bow: [
      PLAYER_ACTION_CLIPS.attack.bow,
      { clip: 'Attack_bow_2', lengthSeconds: 0.7, contactSeconds: 0.4 },
    ],
  };

/**
 * Heavy/finisher clips (AD-9008: sword, staff). Delivered and resolvable by name; NOT yet
 * triggered — nothing in the game marks a swing "heavy" today (a charged attack or an
 * ability would), so they wait for that trigger rather than being forced into the chain.
 */
export const PLAYER_ATTACK_HEAVY: Readonly<Partial<Record<WeaponClass, PlayerActionClipSpec>>> = {
  sword: { clip: 'Attack_sword_1h_heavy', lengthSeconds: 1.6, contactSeconds: 0.867 },
  staff: { clip: 'Attack_staff_heavy', lengthSeconds: 1.9, contactSeconds: 0.933 },
};

/** How many combo steps a class has (1 when unknown). */
export function comboLength(variant: string | null | undefined): number {
  if (variant == null || !Object.prototype.hasOwnProperty.call(PLAYER_ATTACK_COMBOS, variant))
    return 1;
  return PLAYER_ATTACK_COMBOS[variant as WeaponClass].length;
}

/**
 * Held-weapon archetype (`items[].presentation.archetype` ?? `.model`, weapon-bases-v1;
 * `gripArchetype`) → class.
 * The archetype is the ONLY client-side signal that tells a warhammer from a sword
 * or a dagger from a shield: shared `WEAPON_TYPES` has no hammer/dagger, and an
 * item's `weaponType` is not projected into the client content pack.
 */
export const WEAPON_CLASS_BY_MODEL: Readonly<Record<string, WeaponClass>> = {
  'sword-1h-base': 'sword',
  'warhammer-2h-base': 'warhammer',
  'bow-base': 'bow',
  'staff-base': 'staff',
  'dagger-offhand-base': 'dagger',
  'shield-base': 'shield',
  'buckler-base': 'shield',
};

/**
 * `gatherResult.gatheringSkill` → gather motion. `hunting` (skinning knife) and
 * `salvaging` (pry bar) are crouched hand-work on the ground, so they borrow
 * `harvest` rather than the overhead strikes — `[tune]`, amend the row if an eyeball
 * disagrees. Every `GATHERING_SKILLS` value maps.
 */
export const GATHER_KIND_BY_SKILL: Readonly<Record<GatheringSkill, GatherKind>> = {
  forestry: 'chop',
  mining: 'mine',
  herbalism: 'harvest',
  hunting: 'harvest',
  salvaging: 'harvest',
};

/** The one craft motion every craft plays (no station kind reaches the client). */
export const DEFAULT_CRAFT_KIND: CraftKind = 'station';

/** The equipped-item facts the class picker reads: its slot + its held archetype. */
export interface EquippedWeaponFact {
  slot: string | null | undefined;
  model: string | null | undefined;
}

/**
 * The class the player attacks with: the MAINHAND weapon's class, else the
 * OFFHAND's (a dagger or shield alone), else `null` (unarmed / unknown ⇒ the
 * generic swing). The basic attack's power comes from the mainhand, so it owns the
 * motion when both hands are full.
 */
export function weaponClassForEquipped(items: readonly EquippedWeaponFact[]): WeaponClass | null {
  for (const slot of ['mainhand', 'offhand']) {
    for (const item of items) {
      if (item.slot !== slot || item.model == null) continue;
      if (!Object.prototype.hasOwnProperty.call(WEAPON_CLASS_BY_MODEL, item.model)) continue;
      const cls = WEAPON_CLASS_BY_MODEL[item.model];
      if (cls !== undefined) return cls;
    }
  }
  return null;
}

/** A gathering skill's motion, or `null` for an absent/unknown skill (the generic gather). */
export function gatherKindForSkill(skill: string | null | undefined): GatherKind | null {
  if (skill == null || skill === '') return null;
  if (!Object.prototype.hasOwnProperty.call(GATHER_KIND_BY_SKILL, skill)) return null; // no `constructor` etc.
  return (GATHER_KIND_BY_SKILL as Readonly<Record<string, GatherKind>>)[skill] ?? null;
}

// ---------------------------------------------------------------------------
// Rate + policy, derived.
// ---------------------------------------------------------------------------

/**
 * The playback rate for one class clip. `attack` (owner ruling 2026-09-25): the rate at
 * which the WHOLE clip ends by the time the server lets the player swing again —
 * `length / cooldown` — so a swing completes before the next one starts and is never
 * cut back to frame 0. Past `ATTACK_RATE_READABLE_MAX` it plays at that cap (or at
 * `contact / cooldown`, whichever is faster: the blow must still land inside the
 * cadence) and the swing gate chains the next one on completion. `gather` / `craft`: 1
 * here — the gather channel sets its own rate (the server paces a channel, not a click).
 */
export function playerActionTimeScale(
  action: PlayerClassAction,
  spec: PlayerActionClipSpec,
): number {
  if (action !== 'attack') return 1;
  const fit = spec.lengthSeconds / PLAYER_ATTACK_COOLDOWN_SECONDS;
  if (fit <= ATTACK_RATE_READABLE_MAX) return fit;
  return Math.max(ATTACK_RATE_READABLE_MAX, spec.contactSeconds / PLAYER_ATTACK_COOLDOWN_SECONDS);
}

/** The whole clip on a clock at its rate — how long a swing owns the pose. */
export function clipWallSeconds(action: PlayerClassAction, spec: PlayerActionClipSpec): number {
  return spec.lengthSeconds / playerActionTimeScale(action, spec);
}

/** Where the blow lands on a clock: clip contact over the rate. */
export function contactWallSeconds(action: PlayerClassAction, spec: PlayerActionClipSpec): number {
  return spec.contactSeconds / playerActionTimeScale(action, spec);
}

/**
 * The policy a class clip plays under: the action's movement rule unchanged, the
 * derived rate, and NO `holdSeconds` — these clips carry authored recoveries, not
 * the slow settle after 1.5 s of `Left_Slash` that its 1.6 s budget trims, and whole they
 * last exactly the 0.6 s cadence at their attack rates (the swing gate never restarts one).
 */
export function playerActionPolicy(
  action: PlayerClassAction,
  spec: PlayerActionClipSpec,
): Readonly<RigActionPolicy> {
  const rate = playerActionTimeScale(action, spec);
  return {
    interruptedByTravel: DEFAULT_ACTION_POLICY[action].interruptedByTravel,
    ...(rate !== 1 ? { timeScale: rate } : {}),
  };
}

// ---------------------------------------------------------------------------
// The resolver.
// ---------------------------------------------------------------------------

/** What a player action resolves to on one rig. */
export interface ResolvedPlayerActionClip {
  /** The rig's own clip name. */
  clip: string;
  /** The policy it plays under. */
  policy: Readonly<RigActionPolicy>;
  /** `class` ⇒ the table matched; `pattern` ⇒ the generic (pre-table) resolution. */
  source: 'class' | 'pattern';
}

function isClassAction(action: RigAction): action is PlayerClassAction {
  return action === 'attack' || action === 'gather' || action === 'craft';
}

/** The table row for (action, variant), or `undefined` when it has none. */
export function playerActionClipSpec(
  action: RigAction,
  variant: string | null | undefined,
): PlayerActionClipSpec | undefined {
  if (!isClassAction(action) || variant == null || variant === '') return undefined;
  const rows = PLAYER_ACTION_CLIPS[action] as Readonly<Record<string, PlayerActionClipSpec>>;
  return Object.prototype.hasOwnProperty.call(rows, variant) ? rows[variant] : undefined;
}

/**
 * Resolve a player action on a rig. The class clip (exact name after the
 * resolver's normalisation — never a prefix) with its derived policy when the table
 * has a row for `variant` AND the rig carries that clip; otherwise the generic
 * pattern clip under `DEFAULT_ACTION_POLICY`; `null` when the rig has neither.
 */
export function resolvePlayerActionClip(
  action: RigAction,
  variant: string | null | undefined,
  clipNames: readonly string[],
  /** AD-670: the attack combo step (0-based, wraps); a step the rig lacks falls back to step 0. */
  comboStep = 0,
): ResolvedPlayerActionClip | null {
  if (action === 'attack' && comboStep > 0 && variant != null) {
    const chain = Object.prototype.hasOwnProperty.call(PLAYER_ATTACK_COMBOS, variant)
      ? PLAYER_ATTACK_COMBOS[variant as WeaponClass]
      : undefined;
    const stepSpec = chain?.[comboStep % chain.length];
    if (stepSpec !== undefined) {
      const want = normaliseClipName(stepSpec.clip);
      const hit = clipNames.find((name) => normaliseClipName(name) === want);
      if (hit !== undefined) {
        return { clip: hit, policy: playerActionPolicy('attack', stepSpec), source: 'class' };
      }
    }
  }
  const spec = playerActionClipSpec(action, variant);
  if (spec !== undefined && isClassAction(action)) {
    const want = normaliseClipName(spec.clip);
    const hit = clipNames.find((name) => normaliseClipName(name) === want);
    if (hit !== undefined) {
      return { clip: hit, policy: playerActionPolicy(action, spec), source: 'class' };
    }
  }
  const clip = resolveActionClipFor(action, clipNames);
  if (clip === null) return null;
  return { clip, policy: DEFAULT_ACTION_POLICY[action], source: 'pattern' };
}

// ---------------------------------------------------------------------------
// Abilities -> swing presentation (AD-670; rig-animation.md §7.7 follow-up 1).
// ---------------------------------------------------------------------------

/** How a damaging ability is presented on the caster's rig. */
export type AbilityMotion = 'heavy' | 'strike';

/**
 * Every DAMAGING ability's swing (AD-670). `heavy` = a big committed blow: the weapon
 * class's heavy clip when the rig carries one (sword, staff — AD-9008), else its step-1
 * attack. `strike` = the class's step-1 attack. Non-damaging abilities (heal, buff,
 * shield, summon, mobility-only, control-only) are absent and play nothing new yet.
 * FRONTEND-LOCAL presentation data (abilities are not in the client pack; adding a
 * content field is a frozen-shape change) — pinned against `content/progression/abilities`
 * by actionClips.test.ts, so a new damaging ability without a row FAILS the suite.
 */
export const ABILITY_MOTION: Readonly<Record<string, AbilityMotion>> = {
  'sword-cleave': 'heavy',
  'sword-riposte': 'strike',
  'shield-bash': 'heavy',
  'staff-arcane-bolt': 'strike',
  'bow-piercing-shot': 'strike',
  'bow-volley': 'strike',
  'calculated-strike': 'heavy',
  'charging-strike': 'heavy',
  'ground-pound': 'heavy',
  'quick-strike': 'strike',
  'enervating-bolt': 'strike',
  'spark-surge': 'heavy',
};

/** The ability's motion, or `null` when it has none (not damaging / unknown). */
export function abilityMotion(abilityId: string): AbilityMotion | null {
  return Object.prototype.hasOwnProperty.call(ABILITY_MOTION, abilityId)
    ? (ABILITY_MOTION[abilityId] ?? null)
    : null;
}

/**
 * Per-ability clips (story `q-stamina-bar-and-ability-animations`, owner playtest 2026-10-23:
 * "Attacks 1, 2, 3 and 4 all use the same animation"). An ability with a row here plays ITS
 * clip for that weapon class when the rig carries it; otherwise its `ABILITY_MOTION` swing.
 * Owner rule (2026-10-23): player clips come from the harvested Meshy humanoid library, never
 * custom-authored. Windows + prep: `tools/rig/ability-clips/spec.json` (the AD-9008 method),
 * shipped in the clips-only `ability-clips` pack; facts are clip seconds at rate 1, as
 * measured by prep.mjs. Sharing a clip between two abilities happens only through this table
 * and `ABILITY_MOTION` (the clip check in actionClips.test.ts refuses a silent fallback).
 */
export const PLAYER_ABILITY_CLIPS: Readonly<
  Record<string, Readonly<Partial<Record<WeaponClass, PlayerActionClipSpec>>>>
> = {
  // Counterstrike 1.6–3.2 s: high guard, a step-in cut, back to guard — the parry's answer.
  'sword-riposte': {
    sword: { clip: 'Ability_riposte_sword', lengthSeconds: 1.6, contactSeconds: 0.767 },
  },
  // Thrust_Slash 0.4–1.2 s: one fast forward stroke, the shortest sword clip.
  'quick-strike': {
    sword: { clip: 'Ability_quick_strike_sword', lengthSeconds: 0.8, contactSeconds: 0.333 },
  },
  // INTERIM (q-charge-ability-anim-and-icon, owner 2026-10-29): the served two-handed overhead
  // slam (`mage_soell_cast_2` 0.3–2.2 s, yaw-ramped to the bladed stance, AD-9008) — raised high,
  // then torso and blade driven forward and down — played over the server's dash (AD-828): "dash
  // forward and slam into an enemy". `Ability_charging_strike_sword` (Skill_01 0–1.0 s) is
  // REJECTED: a root-motion lunge whose Hips X/Z lock leaves the pelvis leading ("a hip thrust").
  // It stays in the pack, unplayed, until the art-side `q-charge-ability-reanimate` replaces this.
  'charging-strike': {
    sword: { clip: 'Attack_staff_heavy', lengthSeconds: 1.9, contactSeconds: 0.933 },
  },
};

/** The ability's own clip spec for weapon class `variant`, or `undefined` when it has none. */
export function playerAbilityClipSpec(
  abilityId: string,
  variant: string | null | undefined,
): PlayerActionClipSpec | undefined {
  if (variant == null || !Object.prototype.hasOwnProperty.call(PLAYER_ABILITY_CLIPS, abilityId))
    return undefined;
  const row = PLAYER_ABILITY_CLIPS[abilityId] as Readonly<Record<string, PlayerActionClipSpec>>;
  return Object.prototype.hasOwnProperty.call(row, variant) ? row[variant] : undefined;
}

/**
 * What ability `abilityId` plays for weapon class `variant`: its own clip (PLAYER_ABILITY_CLIPS)
 * when the rig carries it, else its `ABILITY_MOTION` swing (`resolveAbilityClip`); `null` for an
 * ability with neither (non-damaging / unknown) or a rig with no attack clip at all.
 */
export function resolveAbilityClipFor(
  abilityId: string,
  variant: string | null | undefined,
  clipNames: readonly string[],
): ResolvedPlayerActionClip | null {
  const own = playerAbilityClipSpec(abilityId, variant);
  if (own !== undefined) {
    const want = normaliseClipName(own.clip);
    const hit = clipNames.find((name) => normaliseClipName(name) === want);
    if (hit !== undefined)
      return { clip: hit, policy: playerActionPolicy('attack', own), source: 'class' };
  }
  const motion = abilityMotion(abilityId);
  return motion === null ? null : resolveAbilityClip(motion, variant, clipNames);
}

/**
 * The clip + policy a damaging ability plays for weapon class `variant`: the class's heavy
 * clip for a `heavy` motion when the rig carries it, else the class's step-1 attack (and,
 * with no class or clip, the generic swing — `resolvePlayerActionClip`'s fallback).
 */
export function resolveAbilityClip(
  motion: AbilityMotion,
  variant: string | null | undefined,
  clipNames: readonly string[],
): ResolvedPlayerActionClip | null {
  if (
    motion === 'heavy' &&
    variant != null &&
    Object.prototype.hasOwnProperty.call(PLAYER_ATTACK_HEAVY, variant)
  ) {
    const spec = PLAYER_ATTACK_HEAVY[variant as WeaponClass];
    if (spec !== undefined) {
      const want = normaliseClipName(spec.clip);
      const hit = clipNames.find((name) => normaliseClipName(name) === want);
      if (hit !== undefined)
        return { clip: hit, policy: playerActionPolicy('attack', spec), source: 'class' };
    }
  }
  return resolvePlayerActionClip('attack', variant, clipNames);
}
